import {ComboboxInputProps, OnOptionSubmit} from './types.ts';
import {FC, memo, useCallback, useMemo, useState} from 'react';
import {
  Combobox,
  Input,
  Loader,
  Pill,
  PillProps,
  PillsInput,
  ScrollArea,
  useCombobox,
} from '@mantine/core';
import {DropdownOptionItem, Nullish} from '../../../types';
import {getEnumSelectOptions} from '../../../api/utils.ts';
import {ComboboxUtils} from './utils.ts';
import {ComboboxOption} from './combobox-option.tsx';

type MultiSelectInputProps = {
  value: Nullish<string[]>;
  onChange: (value: Nullish<string[]>) => void;
  searchable?: boolean;
};

export type MultiSelectProps = MultiSelectInputProps & ComboboxInputProps;

export const MultiSelect: FC<MultiSelectProps> = (props) => {
  const {
    comboboxOptions,
    comboboxProps,
    onChange,
    disabled,
    readOnly,
    value,
    data,
    enumObject,
    loading,
    placeholder = 'Select Value',
    searchable,
    description,
    leftSection,
    limit,
    maxDropdownHeight = 250,
    error,
  } = props;

  const [search, setSearch] = useState('');

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    ...comboboxOptions,
  });

  const onOptionSubmit = useCallback<OnOptionSubmit>(
    (_value) => {
      const newValues = value?.includes(_value)
        ? value?.filter((v) => v !== _value)
        : [...(value || []), _value];

      onChange(newValues);
    },
    [onChange, value]
  );

  const handleValueRemove = useCallback(
    (val: string) => {
      const newValues = value?.filter((v) => v !== val) ?? [];
      onChange(newValues);
    },
    [onChange, value]
  );

  const _options = useMemo(
    () =>
      enumObject
        ? getEnumSelectOptions(enumObject)
        : ComboboxUtils.normalizeOptionsData(data),
    [data, enumObject]
  );

  const filteredOptions = useMemo<DropdownOptionItem<string>[]>(
    () => ComboboxUtils.getFilteredOptions(_options, search, limit),
    [_options, search, limit]
  );

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={onOptionSubmit}
      withinPortal={false}
      keepMounted={false}
      {...comboboxProps}
      disabled={disabled}
      readOnly={readOnly}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          error={error}
          pointer
          onClick={searchable ? undefined : () => combobox.toggleDropdown()}
          rightSection={<RightSection loading={loading} />}
          rightSectionPointerEvents={'none'}
          description={description}
          leftSection={leftSection}
        >
          <Pill.Group>
            {value?.length ? (
              value.map((val: string, index: number) => (
                <ValuePill
                  key={`valuePill${index}-value`}
                  value={val}
                  label={
                    filteredOptions.find((x) => x.value === val)?.label ?? ''
                  }
                  withRemoveButton
                  onRemove={() => handleValueRemove(val)}
                />
              ))
            ) : !searchable ? (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            ) : (
              <></>
            )}
            {searchable && (
              <Combobox.EventsTarget>
                <PillsInput.Field
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={search}
                  placeholder={placeholder}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Backspace' && search.length === 0) {
                      event.preventDefault();
                      handleValueRemove(String(value?.[value?.length - 1]));
                    }
                  }}
                />
              </Combobox.EventsTarget>
            )}
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize type="scroll" mah={maxDropdownHeight}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(
                (item: DropdownOptionItem<string>, index: number) => (
                  <ComboboxOption
                    key={`option${index}-${item.value}`}
                    active={value?.includes(item.value)}
                    {...item}
                  />
                )
              )
            ) : (
              <Combobox.Empty>No Results</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

type ValuePillProps = PillProps & {value: string; label: string};
const ValuePill = memo<ValuePillProps>(({onRemove, label}) => {
  return (
    <Pill withRemoveButton onRemove={onRemove}>
      {label}
    </Pill>
  );
});

type RightSectionProps = {
  loading?: boolean;
};
const RightSection = memo<RightSectionProps>(({loading}) => {
  return loading ? <Loader size={'xs'} /> : <Combobox.Chevron />;
});